import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Loader2, ShieldCheck, ArrowRight, Lock, CalendarIcon, Check,
} from "lucide-react";
import { waLink } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  { v: "residential", l: "Residential" },
  { v: "apartment", l: "Apartment / Housing Society" },
  { v: "office", l: "Commercial Office" },
  { v: "shop", l: "Retail Shop" },
  { v: "warehouse", l: "Warehouse" },
  { v: "factory", l: "Factory / Industrial" },
  { v: "school", l: "School / College" },
  { v: "hospital", l: "Hospital" },
  { v: "hotel", l: "Hotel / Restaurant" },
  { v: "other", l: "Other" },
];
const EXISTING_CCTV = [
  { v: "yes", l: "Yes" },
  { v: "no", l: "No" },
  { v: "planning", l: "Planning to Install" },
];
const CAMERA_RANGES = [
  { v: "none", l: "None" },
  { v: "1-4", l: "1 – 4" },
  { v: "5-8", l: "5 – 8" },
  { v: "9-16", l: "9 – 16" },
  { v: "17-32", l: "17 – 32" },
  { v: "32+", l: "32+" },
];
const CAMERA_BRANDS = [
  "CP Plus", "Hikvision", "Dahua", "UNV (Uniview)", "TP-Link / VIGI",
  "Axis", "Bosch", "Honeywell", "Panasonic", "Sony",
  "Secureye", "Prama", "Qubo", "Other", "Not Sure",
];
const CAMERA_TYPES = [
  "Analog (DVR)", "IP Camera (NVR)", "Wi-Fi Camera", "PTZ Camera",
  "Mixed (Analog & IP)", "Not Sure",
];
const PERIMETER_OPTS = [
  "Boundary Wall", "Fencing", "Compound Wall with Gate",
  "Open Premises (No Boundary)", "Mixed", "Not Sure",
];
const SERVICES = [
  "CCTV Remote Monitoring",
  "CCTV Health Check",
  "Security Audit",
  "Alarm Monitoring",
  "CCTV Installation",
  "CCTV Upgrade",
  "Senior Citizen Safety Monitoring",
  "Holiday Property Monitoring",
  "Other",
];
const AUDIT_MODE = [
  { v: "onsite", l: "On-Site Visit" },
  { v: "online", l: "Online Video Consultation" },
  { v: "phone", l: "Phone Consultation" },
];
const TIME_SLOTS = [
  { v: "9-12", l: "9:00 AM – 12:00 PM" },
  { v: "12-15", l: "12:00 PM – 3:00 PM" },
  { v: "15-18", l: "3:00 PM – 6:00 PM" },
  { v: "18-20", l: "6:00 PM – 8:00 PM" },
];

const buildLeadWaMessage = (f) => {
  const lines = [
    `*New FREE Security Audit Request — Secure Watch*`,
  ];
  if (f.ticket_number) lines.push(`🎫 *Ref:* ${f.ticket_number}`);
  lines.push(
    ``,
    `👤 *Name:* ${f.name}`,
    `📞 *Phone:* ${f.phone}`,
  );
  if (f.email) lines.push(`✉️ *Email:* ${f.email}`);
  if (f.property_type) lines.push(`🏠 *Property Type:* ${f.property_type}`);
  if (f.existing_cctv) lines.push(`📹 *Existing CCTV:* ${f.existing_cctv}`);
  if (f.cameras) lines.push(`🎥 *No. of Cameras:* ${f.cameras}`);
  if (f.camera_brand) lines.push(`🏷️ *Camera Brand:* ${f.camera_brand}`);
  if (f.camera_type) lines.push(`🎞️ *Camera Type:* ${f.camera_type}`);
  if (f.perimeter_security) lines.push(`🧱 *Perimeter Security:* ${f.perimeter_security}`);
  if (f.services?.length) lines.push(`⭐ *Services:* ${f.services.join(", ")}`);
  if (f.audit_mode) lines.push(`🧭 *Audit Mode:* ${f.audit_mode}`);
  if (f.preferred_date) lines.push(`📅 *Preferred Date:* ${f.preferred_date}`);
  if (f.preferred_time) lines.push(`⏰ *Preferred Time:* ${f.preferred_time}`);
  if (f.property_address) lines.push(``, `📍 *Address:*`, f.property_address);
  if (f.message) lines.push(``, `📝 *Security Concerns:*`, f.message);
  lines.push(``, `_Please contact me at the earliest to schedule my FREE security audit._`);
  return lines.join("\n");
};

const initial = {
  name: "",
  phone: "",
  email: "",
  property_type: "",
  existing_cctv: "",
  cameras: "",
  camera_brand: "",
  camera_type: "",
  perimeter_security: "",
  services: [],
  audit_mode: "",
  preferred_date: "",
  preferred_time: "",
  property_address: "",
  message: "",
  consent: false,
};

export default function LeadForm() {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [waHref, setWaHref] = useState("");
  const [ticket, setTicket] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const toggleService = (svc) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(svc)
        ? f.services.filter((s) => s !== svc)
        : [...f.services, svc],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    if (!/^\+?[0-9\s-]{6,15}$/.test(form.phone.trim())) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (!form.property_type) return toast.error("Please select your property type.");
    if (!form.existing_cctv) return toast.error("Please tell us about your existing CCTV.");
    if (!form.camera_brand) return toast.error("Please select your camera brand.");
    if (!form.camera_type) return toast.error("Please select your camera type.");
    if (!form.perimeter_security) return toast.error("Please tell us about your perimeter security.");
    if (!form.services.length) return toast.error("Please select at least one service you're interested in.");
    if (!form.consent) return toast.error("Please agree to be contacted so we can call you back.");

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, { ...form, source: "audit-form" });
      const tk = data?.ticket_number || "";
      const wa = waLink(buildLeadWaMessage({ ...form, ticket_number: tk }));
      setTicket(tk);
      setWaHref(wa);
      setSent(true);
      setForm(initial);
      setDate(null);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try calling us instead.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="lead-form-success"
        className="rounded-2xl border border-brand-cyan/40 bg-brand-cyan/5 p-8 text-center"
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-brand-cyan/20 flex items-center justify-center mb-4">
          <ShieldCheck className="h-7 w-7 text-brand-cyan" />
        </div>
        <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white">Thank you!</h3>
        <p className="text-white/75 mt-3 max-w-lg mx-auto leading-relaxed">
          Your request has been received. A Secure Watch security specialist will contact you shortly to
          schedule your <strong className="text-brand-cyan">FREE Security Audit</strong> and discuss the
          best security solutions for your property.
        </p>

        {ticket && (
          <div className="mt-6 mx-auto max-w-sm rounded-xl border border-brand-cyan/40 bg-black/40 px-4 py-3" data-testid="lead-form-ticket">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-cyan/80">Service Request Reference</div>
            <div className="mt-1 flex items-center justify-center gap-2">
              <span className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-cyan tracking-wider text-glow">
                {ticket}
              </span>
              <button
                type="button"
                data-testid="lead-form-ticket-copy"
                onClick={() => { navigator.clipboard.writeText(ticket); toast.success("Reference copied"); }}
                className="text-[11px] px-2 py-1 rounded-md bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/25"
              >Copy</button>
            </div>
            <p className="text-[11px] text-white/50 mt-2">
              Please keep this number handy — we'll use it when we call you back.
            </p>
          </div>
        )}

        {waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="lead-form-wa-followup"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] hover:bg-[#22c55e] text-white font-bold px-5 h-11 shadow-[0_0_20px_rgba(37,211,102,0.4)]"
          >
            <svg viewBox="0 0 32 32" className="h-4 w-4 fill-white" aria-hidden="true">
              <path d="M16.02 4C9.39 4 4 9.39 4 16c0 2.11.56 4.15 1.62 5.94L4 28l6.24-1.62A11.94 11.94 0 0 0 16.02 28C22.65 28 28 22.63 28 16.02 28 9.4 22.65 4 16.02 4z"/>
            </svg>
            Also notify us on WhatsApp
          </a>
        )}
        <div className="mt-3">
          <Button
            onClick={() => { setSent(false); setWaHref(""); setTicket(""); }}
            variant="outline"
            data-testid="lead-form-reset"
            className="border-white/20 text-white hover:bg-white/10"
          >
            Send another request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      data-testid="lead-form"
      className="relative rounded-2xl border border-white/10 bg-[#0F0F13] p-6 lg:p-8"
    >
      <div className="absolute -top-3 left-6 flex items-center gap-2 px-3 py-1 rounded-full bg-brand-cyan text-black text-[11px] font-bold tracking-wider uppercase">
        <span className="h-2 w-2 rounded-full bg-black pulse-dot" />
        FREE Security Audit
      </div>

      <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mt-2">
        Request Your Free Security Audit
      </h3>
      <p className="text-sm text-white/60 mt-2 leading-relaxed">
        Protect your property with a <strong className="text-white">FREE professional security assessment</strong>.
        Our experts will evaluate your CCTV system, identify security gaps, and recommend the best surveillance
        solutions — at no cost.
      </p>

      <div className="grid gap-4 mt-6 sm:grid-cols-2">
        <Field label="Full Name *">
          <Input data-testid="lead-input-name" value={form.name} onChange={set("name")}
            placeholder="Anurag Sharma" className={inputCx} />
        </Field>
        <Field label="Mobile Number *">
          <Input data-testid="lead-input-phone" value={form.phone} onChange={set("phone")}
            placeholder="+91 98XXXXXXXX" inputMode="tel" className={inputCx} />
        </Field>

        <Field label="Email Address" full>
          <Input data-testid="lead-input-email" type="email" value={form.email} onChange={set("email")}
            placeholder="you@example.com" className={inputCx} />
        </Field>

        <Field label="Property Type *">
          <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
            <SelectTrigger data-testid="lead-input-property" className={triggerCx}><SelectValue placeholder="Select property type" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {PROPERTY_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Do You Have Existing CCTV? *">
          <Select value={form.existing_cctv} onValueChange={(v) => setForm({ ...form, existing_cctv: v })}>
            <SelectTrigger data-testid="lead-input-existing" className={triggerCx}><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {EXISTING_CCTV.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Number of Cameras" full>
          <Select value={form.cameras} onValueChange={(v) => setForm({ ...form, cameras: v })}>
            <SelectTrigger data-testid="lead-input-cameras" className={triggerCx}><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {CAMERA_RANGES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Camera Brand *">
          <Select value={form.camera_brand} onValueChange={(v) => setForm({ ...form, camera_brand: v })}>
            <SelectTrigger data-testid="lead-input-camera-brand" className={triggerCx}><SelectValue placeholder="Select brand" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {CAMERA_BRANDS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Camera Type *">
          <Select value={form.camera_type} onValueChange={(v) => setForm({ ...form, camera_type: v })}>
            <SelectTrigger data-testid="lead-input-camera-type" className={triggerCx}><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {CAMERA_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Perimeter Security *" full>
          <Select value={form.perimeter_security} onValueChange={(v) => setForm({ ...form, perimeter_security: v })}>
            <SelectTrigger data-testid="lead-input-perimeter" className={triggerCx}><SelectValue placeholder="Select perimeter type" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {PERIMETER_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        {/* Multi-select services */}
        <div className="sm:col-span-2">
          <Label className="text-white/80 text-xs">Services Interested In * <span className="text-white/40 font-normal">(select all that apply)</span></Label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {SERVICES.map((svc) => {
              const active = form.services.includes(svc);
              return (
                <button
                  type="button"
                  key={svc}
                  data-testid={`svc-${svc.toLowerCase().replace(/\s|\//g, "-")}`}
                  onClick={() => toggleService(svc)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                    active
                      ? "border-brand-cyan/60 bg-brand-cyan/10 text-white"
                      : "border-white/10 bg-black/30 text-white/70 hover:bg-white/5"
                  }`}
                >
                  <span className={`h-4 w-4 rounded flex items-center justify-center border ${
                    active ? "bg-brand-cyan border-brand-cyan text-black" : "border-white/25 bg-transparent"
                  }`}>
                    {active && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1">{svc}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Field label="Preferred Audit Mode">
          <Select value={form.audit_mode} onValueChange={(v) => setForm({ ...form, audit_mode: v })}>
            <SelectTrigger data-testid="lead-input-audit-mode" className={triggerCx}><SelectValue placeholder="Select mode" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {AUDIT_MODE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Preferred Time">
          <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
            <SelectTrigger data-testid="lead-input-preferred-time" className={triggerCx}><SelectValue placeholder="Select slot" /></SelectTrigger>
            <SelectContent className={contentCx}>
              {TIME_SLOTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Preferred Date" full>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="lead-input-preferred-date"
                className={`${triggerCx} w-full flex items-center gap-2 h-10 justify-start`}
              >
                <CalendarIcon className="h-4 w-4 text-brand-cyan" />
                <span className={date ? "text-white" : "text-white/40"}>
                  {date ? formatDMY(date) : "Pick a date"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#0F0F13] border-white/10" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => {
                  setDate(d);
                  setForm((f) => ({ ...f, preferred_date: d ? formatDMY(d) : "" }));
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                className="text-white"
              />
            </PopoverContent>
          </Popover>
        </Field>

        <Field label="Property Address" full>
          <Textarea data-testid="lead-input-property-address" rows={2}
            value={form.property_address} onChange={set("property_address")}
            placeholder="Flat / House no., street, area, city, PIN" className={inputCx} />
        </Field>

        <Field label="Tell Us About Your Security Concerns" full>
          <Textarea data-testid="lead-input-message" rows={3}
            value={form.message} onChange={set("message")}
            placeholder="Example: Blind spots, theft incidents, CCTV not working, poor night vision, internet issues, monitoring requirements, etc."
            className={inputCx} />
        </Field>
      </div>

      {/* Consent */}
      <label htmlFor="consent-cb" className="mt-6 flex items-start gap-3 rounded-lg border border-white/10 bg-black/30 p-3 cursor-pointer">
        <Checkbox
          id="consent-cb"
          data-testid="lead-input-consent"
          checked={form.consent}
          onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))}
          className="mt-0.5 border-brand-cyan/60 data-[state=checked]:bg-brand-cyan data-[state=checked]:text-black"
        />
        <span className="text-[13px] text-white/75 leading-relaxed">
          I agree to be contacted by Secure Watch regarding my free security audit and related security services.
        </span>
      </label>

      <Button
        type="submit"
        data-testid="lead-form-submit"
        disabled={loading}
        className="mt-5 w-full h-12 bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold text-base shadow-[0_0_30px_rgba(0,229,255,0.35)] hover:-translate-y-0.5 transition-transform"
      >
        {loading ? (
          <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Sending…</>
        ) : (
          <><Lock className="mr-2 h-4 w-4" />Book My FREE Security Audit<ArrowRight className="ml-2 h-5 w-5" /></>
        )}
      </Button>

      <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
        We respect your privacy. Your details are used only to schedule your audit and are never shared.
      </p>
    </form>
  );
}

/* ----- helpers ----- */
const inputCx = "mt-1 bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand-cyan";
const triggerCx = "mt-1 bg-black/40 border-white/10 text-white";
const contentCx = "bg-[#0F0F13] border-white/10 text-white";

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-white/80 text-xs">{label}</Label>
      {children}
    </div>
  );
}

function formatDMY(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}
